import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

interface Props {
  /**
   * Uppercase section heading — ЗАКАЗ / ГРУЗ / АДРЕС. Rendered as an `<h3>` so
   * the columns are real landmarks for a screen-reader user rather than three
   * undifferentiated runs of form controls.
   */
  heading: string;
  /** Renders the red `*` after the heading (АДРЕС *). */
  required?: boolean;
  children: ReactNode;
  className?: string;
  /**
   * Lay the column out as a flex column so ONE child can absorb the leftover
   * height via `flex-1` (owner, 2026-08-24: the driver-notes box must reach the
   * same bottom edge as the neighbouring columns instead of trailing off).
   *
   * Opt-in rather than the default: the other columns rely on `space-y` and
   * intrinsic heights, and switching them all to flex would change spacing
   * everywhere for the sake of one box. `gap-2.5` mirrors the `space-y-2.5`
   * step exactly, so a flex column is spaced identically to a normal one.
   */
  fill?: boolean;
  /**
   * Visual position of this column via CSS `order` (owner, 2026-08-24).
   *
   * The order-detail modal renders ONE set of three columns for two modes, and
   * the two want opposite orders: the edit FORM must read ЗАКАЗ → ГРУЗ → АДРЕС to
   * match «Новый заказ», while the VIEW reads КУДА → ДОСТАВКА → ЖУРНАЛ. The JSX is
   * ordered for the form — because a form's DOM order IS its tab order, and a
   * CSS-only reorder there would send focus jumping between columns. View mode has
   * no inputs to tab through, so shuffling it visually costs nothing.
   *
   * Use this ONLY for read-only content. For anything focusable, move the JSX.
   */
  order?: 1 | 2 | 3;
}

/**
 * One column of {@link ModalThreeColumn}, with its section heading.
 *
 * `min-w-0` is load-bearing and not cosmetic: a grid item defaults to
 * `min-width: auto`, so one long unbroken word (an address, an email) makes the
 * track wider than its `1fr` share and the whole dialog scrolls sideways.
 */
export function ModalColumn({
  heading,
  required = false,
  children,
  className,
  fill = false,
  order,
}: Props) {
  return (
    /*
      Spacing is part of the height budget, not decoration (§3.2). Measured at
      1280×800: `space-y-4` + `p-6` overflowed the ~596 px of column space by
      85 px. The tighter step below 2xl buys that back; the roomier one applies
      only where the budget actually affords it.
    */
    <section
      className={cn(
        "min-w-0 p-4 2xl:p-6",
        // A column that cannot fit its content SCROLLS rather than swallowing it.
        // The grid is `lg:overflow-visible` to honour "no internal scrolling at
        // 1280", but the form wrapping it is `overflow-hidden`, so anything that
        // DID overflow became unreachable rather than merely clipped — with
        // parcels enabled, «Добавить посылку» sat 72px past the edge with no way
        // to scroll to it. Nothing overflows in the normal case, so nothing
        // scrolls and the guarantee stands; this only refuses to hide content.
        //
        // `min-h-0` is what makes it work inside the grid: a grid item's default
        // `min-height: auto` refuses to shrink below its content, so the column
        // would grow past its track and overflow the FORM instead of scrolling
        // itself.
        fill
          ? // `fill` columns are `h-full` flex parents whose children claim the
            // slack with `flex-1`. Adding `max-h-full` here fought that: the
            // notes textarea collapsed and the parcels block rendered ON TOP of
            // it. Such a column manages its own height, so it only needs the
            // scroll affordance, never a height cap.
            //
            // All three only from `lg`, where the columns sit side by side.
            // Below it they stack inside the grid's own scroller, and an
            // `h-full min-h-0` column collapsed to its heading: measured at
            // 375px, 32px tall over 631px of content, hiding the address, the
            // НП line, the map and the picker (Sprint 228 UI review). Stacked, a
            // column takes its natural height and the grid scrolls.
            "flex flex-col gap-2.5 lg:h-full lg:min-h-0 lg:overflow-y-auto 2xl:gap-4"
          : "space-y-2.5 lg:max-h-full lg:min-h-0 lg:overflow-y-auto 2xl:space-y-4",
        // Static class names, never `order-${n}` — Tailwind scans source text, so
        // an interpolated name is never generated and the rule silently does not
        // exist. That failure is invisible: the column just keeps source order.
        order === 1 && "lg:order-1",
        order === 2 && "lg:order-2",
        order === 3 && "lg:order-3",
        className,
      )}
    >
      <h3 className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
        {heading}
        {required && (
          <span className="ml-1 text-brand-danger" aria-hidden="true">
            *
          </span>
        )}
      </h3>
      {children}
    </section>
  );
}

/**
 * A secondary heading inside a column — used by column 2, which carries both
 * ГРУЗ and КЛИЕНТ under one column heading.
 */
export function ModalSubheading({ children }: { children: ReactNode }) {
  return (
    <h4 className="pt-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
      {children}
    </h4>
  );
}
